/**
 * Settings ▸ Margin (admin): what customers were billed, what it cost us,
 * and the difference — live, so a rate can be moved before it loses money.
 *
 * Both numbers come off the same ledger rows (billed at house rates, cost
 * from the cost book at charge time), so nothing here is re-derived. Contract
 * fees are added to revenue prorated per bucket. Rows whose model had no
 * cost rates are COUNTED and shown as "unpriced" — a zero there would read
 * as free, which is the one lie this screen must not tell.
 *
 * One axis (dollars), three series, a legend, a table under the chart, and a
 * tooltip on hover. Quota gauges for flat-plan providers, and a what-if that
 * re-prices the last 30 days under the draft rate card.
 *
 * Hidden from customers by the admin check on the route AND the endpoint;
 * the internals reveal is not what protects it.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../services/api';
import { PRODUCT_INFO } from '../shell/products';
import { formatPeriodHeader, isCurrentOrFuturePeriod, shiftPeriod, type Period } from './usagePeriods';
import type { LLMRate, MarginBreakdown, MarginReport, Product, ProviderQuota, WhatIfResult } from '../types';

// Categorical hues in FIXED order from the validated reference palette (light /
// dark). Series identity: 1 billed, 2 cost, 3 margin; the cost stack reuses the
// same slots by basis so a color never changes meaning across the two charts.
const LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];
const DARK = ['#3987e5', '#d95926', '#199e70', '#c98500'];

const BASIS_LABEL: Record<string, string> = {
  metered: 'Metered (per token)',
  flat_plan: 'Flat plan (imputed)',
  self_hosted: 'Self-hosted (imputed)',
  network: 'Network',
  unknown: 'Unknown basis',
};
const BASIS_ORDER = ['metered', 'flat_plan', 'self_hosted', 'network', 'unknown'];

function usd(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
}

function useDark(): boolean {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function MoneyTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-sm">
      <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">{label}</div>
      {payload.map((entry: { name: string; value: number; color: string }, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-600 dark:text-gray-300">{entry.name}:</span>
          <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{usd(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

function BreakdownTable({ title, rows, unit }: { title: string; rows: MarginBreakdown[]; unit: 'tokens' | 'calls' | 'both' }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{title}</h4>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400">
            <tr>
              <th className="text-left font-medium px-3 py-1.5">{title}</th>
              <th className="text-right font-medium px-3 py-1.5">billed</th>
              <th className="text-right font-medium px-3 py-1.5">cost</th>
              <th className="text-right font-medium px-3 py-1.5">margin</th>
              <th className="text-right font-medium px-3 py-1.5">credits</th>
              {unit !== 'calls' && <th className="text-right font-medium px-3 py-1.5">tokens</th>}
              {unit !== 'tokens' && <th className="text-right font-medium px-3 py-1.5">calls</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{r.label}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{usd(r.billed_usd)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{usd(r.cost_usd)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${r.margin_usd < 0 ? 'text-red-700 dark:text-red-300' : ''}`}>
                  {usd(r.margin_usd)}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{compact(r.credits)}</td>
                {unit !== 'calls' && <td className="px-3 py-1.5 text-right tabular-nums">{compact(r.tokens)}</td>}
                {unit !== 'tokens' && <td className="px-3 py-1.5 text-right tabular-nums">{r.calls}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminMargin() {
  const dark = useDark();
  const colors = dark ? DARK : LIGHT;
  const [period, setPeriod] = useState<Period>('month');
  const [periodDate, setPeriodDate] = useState<string | undefined>(undefined);
  const [product, setProduct] = useState<string>('');
  const [report, setReport] = useState<MarginReport | null>(null);
  const [quotas, setQuotas] = useState<ProviderQuota[]>([]);
  const [error, setError] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [whatIf, setWhatIf] = useState<WhatIfResult | null>(null);
  const [draft, setDraft] = useState<{ usd: string; std: Record<keyof LLMRate, string>; serp: string; page: string; byok: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, q] = await Promise.all([
        api.adminGetMargin({ period, periodDate, product: product || undefined }),
        api.adminGetQuotas(),
      ]);
      setReport(r);
      setQuotas(q);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the margin report');
    }
  }, [period, periodDate, product]);

  useEffect(() => {
    void load();
  }, [load]);

  // The what-if starts from the active rate card, so a change is one number.
  useEffect(() => {
    api
      .adminGetPriceBook()
      .then((pb) => {
        const std = pb.active.llm_rates.standard ?? pb.defaults.llm_rates.standard;
        setDraft({
          usd: String(pb.active.usd_per_credit),
          std: {
            input_per_1k: String(std?.input_per_1k ?? 0),
            output_per_1k: String(std?.output_per_1k ?? 0),
            cached_input_per_1k: String(std?.cached_input_per_1k ?? 0),
          },
          serp: String(pb.active.network_rates.LOOKUP_SERP ?? 0),
          page: String(pb.active.network_rates.LOOKUP_PAGE ?? 0),
          byok: String(pb.active.byok_llm_multiplier),
        });
      })
      .catch(() => undefined);
  }, []);

  const runWhatIf = async () => {
    if (!draft) return;
    try {
      const pb = await api.adminGetPriceBook();
      const base = pb.active;
      const std: LLMRate = {
        input_per_1k: Number(draft.std.input_per_1k),
        output_per_1k: Number(draft.std.output_per_1k),
        cached_input_per_1k: Number(draft.std.cached_input_per_1k),
      };
      const ratio = base.llm_rates.standard ? std.input_per_1k / (base.llm_rates.standard.input_per_1k || 1) : 1;
      const scaled = (r: LLMRate | undefined): LLMRate =>
        r
          ? { input_per_1k: r.input_per_1k * ratio, output_per_1k: r.output_per_1k * ratio, cached_input_per_1k: r.cached_input_per_1k * ratio }
          : std;
      const out = await api.adminWhatIf({
        usd_per_credit: Number(draft.usd),
        llm_rates: {
          economy: scaled(base.llm_rates.economy),
          standard: std,
          premium: scaled(base.llm_rates.premium),
        },
        network_rates: {
          ...base.network_rates,
          LOOKUP_SERP: Number(draft.serp),
          LOOKUP_PAGE: Number(draft.page),
        },
        byok_llm_multiplier: Number(draft.byok),
        byok_network_multiplier: base.byok_network_multiplier,
        days: 30,
      });
      setWhatIf(out);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'What-if failed');
    }
  };

  const t = report?.totals;
  const chartRows = (report?.buckets ?? []).map((b) => ({
    label: b.label,
    Billed: b.billed_usd + b.contract_usd,
    Cost: b.cost_usd,
    Margin: b.margin_usd,
    ...Object.fromEntries(BASIS_ORDER.map((k) => [BASIS_LABEL[k], b.cost_by_basis[k] ?? 0])),
  }));
  const basesPresent = BASIS_ORDER.filter((k) => (report?.buckets ?? []).some((b) => (b.cost_by_basis[k] ?? 0) > 0));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center gap-2">
        {(['hour', 'day', 'month', 'year'] as Period[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => {
              setPeriod(p);
              setPeriodDate(undefined);
            }}
            className={`px-2.5 py-1 text-sm rounded border ${
              period === p
                ? 'bg-gray-800 text-white border-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
            }`}
          >
            {p}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-gray-300 dark:bg-gray-600" aria-hidden />
        <button
          type="button"
          disabled={!report?.period_start}
          onClick={() => report?.period_start && setPeriodDate(shiftPeriod(report.period_start, period, -1))}
          className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          aria-label="Previous period"
        >
          ←
        </button>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[8rem] text-center">
          {report?.period_start ? formatPeriodHeader(report.period_start, period) : '-'}
        </span>
        <button
          type="button"
          disabled={!report?.period_start || isCurrentOrFuturePeriod(report.period_start, period)}
          onClick={() => report?.period_start && setPeriodDate(shiftPeriod(report.period_start, period, 1))}
          className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          aria-label="Next period"
        >
          →
        </button>
        <select
          aria-label="Product"
          value={product}
          onChange={(e) => {
            setProduct(e.target.value);
            setPeriodDate(undefined);
          }}
          className="ml-auto border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-sm"
        >
          <option value="">All products</option>
          {(Object.keys(PRODUCT_INFO) as Product[]).map((p) => (
            <option key={p} value={p}>
              {PRODUCT_INFO[p].name}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {t && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            ['Billed', t.billed_usd + t.contract_usd, `${usd(t.billed_usd)} usage + ${usd(t.contract_usd)} contracts`],
            ['Cost', t.cost_usd, `${usd(t.cost_llm_usd)} AI + ${usd(t.cost_network_usd)} network`],
            ['Margin', t.margin_usd, t.billed_usd + t.contract_usd > 0 ? `${Math.round((100 * t.margin_usd) / (t.billed_usd + t.contract_usd))}% of billed` : ''],
            ['Credits', t.credits, `${compact(t.input_tokens + t.output_tokens)} tokens · ${t.calls} calls`],
          ].map(([label, value, sub]) => (
            <div key={String(label)} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className={`text-xl font-semibold tabular-nums ${label === 'Margin' && Number(value) < 0 ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'}`}>
                {label === 'Credits' ? compact(Number(value)) : usd(Number(value))}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">{sub}</p>
            </div>
          ))}
        </div>
      )}

      {t && t.unpriced > 0 && (
        <p className="text-sm text-amber-800 dark:text-amber-200" data-testid="unpriced-warning">
          {t.unpriced} charge{t.unpriced === 1 ? '' : 's'} ran on a model with no cost rates, so the cost shown is LOW by that much. Set
          dollars-per-million on the model under Settings ▸ AI ▸ Models.
        </p>
      )}

      {report && report.buckets.length > 0 && (
        <section>
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">Billed, cost and margin</h3>
            <button type="button" onClick={() => setShowTable((s) => !s)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
              {showTable ? 'Hide table' : 'Show as table'}
            </button>
          </div>
          <div className="h-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartRows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={dark ? '#2c2c2a' : '#e1e0d9'} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: dark ? '#383835' : '#c3c2b7' }} tickLine={false} />
                <YAxis tickFormatter={(v) => `$${compact(v)}`} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={56} />
                <Tooltip content={<MoneyTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Billed" stroke={colors[0]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="Cost" stroke={colors[1]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="Margin" stroke={colors[2]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {showTable && (
            <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium">bucket</th>
                    <th className="text-right px-3 py-1.5 font-medium">billed</th>
                    <th className="text-right px-3 py-1.5 font-medium">cost</th>
                    <th className="text-right px-3 py-1.5 font-medium">margin</th>
                    <th className="text-right px-3 py-1.5 font-medium">tokens</th>
                    <th className="text-right px-3 py-1.5 font-medium">calls</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {report.buckets.map((b) => (
                    <tr key={b.label}>
                      <td className="px-3 py-1 text-gray-800 dark:text-gray-200">{b.label}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{usd(b.billed_usd + b.contract_usd)}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{usd(b.cost_usd)}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{usd(b.margin_usd)}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{compact(b.input_tokens + b.output_tokens)}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{b.calls}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {report && basesPresent.length > 0 && (
        <section>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Cost by how it costs us</h3>
          <div className="h-48 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={dark ? '#2c2c2a' : '#e1e0d9'} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: dark ? '#383835' : '#c3c2b7' }} tickLine={false} />
                <YAxis tickFormatter={(v) => `$${compact(v)}`} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={56} />
                <Tooltip content={<MoneyTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {basesPresent.map((k, i) => (
                  <Bar key={k} dataKey={BASIS_LABEL[k]} stackId="cost" fill={colors[i % colors.length]} stroke={dark ? '#1a1a19' : '#fcfcfb'} strokeWidth={2} isAnimationActive={false} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Flat-plan and self-hosted costs are imputed from the rates you set on those models; metered is the vendor's bill.
          </p>
        </section>
      )}

      {quotas.length > 0 && (
        <section>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Provider quotas</h3>
          <ul className="grid gap-3 md:grid-cols-2">
            {quotas.map((q) => (
              <li key={q.provider_id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {q.name}
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-gray-400">{q.cost_basis.replace('_', ' ')}</span>
                  {q.plan_monthly_usd !== null && <span className="ml-2 text-xs text-gray-500">{usd(q.plan_monthly_usd)}/mo</span>}
                </p>
                <ul className="mt-2 space-y-1.5">
                  {q.windows.map((w) => (
                    <li key={w.window} className="text-xs">
                      <div className="flex justify-between text-gray-600 dark:text-gray-300">
                        <span>this {w.window}</span>
                        <span className="tabular-nums">
                          {compact(w.used_tokens)}
                          {w.quota_tokens !== null ? ` / ${compact(w.quota_tokens)} tokens` : ' tokens · no quota'}
                          {w.pct !== null && ` · ${w.pct}%`}
                        </span>
                      </div>
                      {w.quota_tokens !== null && (
                        <div className="mt-0.5 h-1.5 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden" role="progressbar" aria-valuenow={w.pct ?? 0} aria-valuemin={0} aria-valuemax={100}>
                          <div
                            className="h-full"
                            style={{
                              width: `${Math.min(100, w.pct ?? 0)}%`,
                              // Status color only when the quota is nearly gone; identity otherwise.
                              backgroundColor: (w.pct ?? 0) >= 90 ? '#d03b3b' : (w.pct ?? 0) >= 75 ? '#fab219' : colors[0],
                            }}
                          />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      )}

      {report && (
        <section className="grid gap-6 md:grid-cols-2">
          <BreakdownTable title="By product" rows={report.by_product} unit="both" />
          <BreakdownTable title="By customer" rows={report.by_user} unit="both" />
          <BreakdownTable title="By model" rows={report.by_model} unit="tokens" />
          <BreakdownTable title="By pipeline version" rows={report.by_version} unit="tokens" />
        </section>
      )}

      {draft && (
        <section className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">What if the rate card were…</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Re-prices the last 30 days of charges under these numbers (economy and premium scale with standard). Saving is on the Billing tab.
          </p>
          <div className="flex flex-wrap items-end gap-3 text-xs">
            {(
              [
                ['$ / credit', 'usd'],
                ['standard in / 1k', 'in'],
                ['standard out / 1k', 'out'],
                ['search / call', 'serp'],
                ['page / call', 'page'],
                ['own-key ×', 'byok'],
              ] as const
            ).map(([label, key]) => (
              <label key={key} className="flex flex-col gap-0.5">
                <span className="text-gray-600 dark:text-gray-300">{label}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={key === 'in' ? draft.std.input_per_1k : key === 'out' ? draft.std.output_per_1k : draft[key as 'usd' | 'serp' | 'page' | 'byok']}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDraft((d) =>
                      d
                        ? key === 'in'
                          ? { ...d, std: { ...d.std, input_per_1k: v } }
                          : key === 'out'
                            ? { ...d, std: { ...d.std, output_per_1k: v } }
                            : { ...d, [key]: v }
                        : d,
                    );
                  }}
                  className="w-24 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-right tabular-nums"
                />
              </label>
            ))}
            <button type="button" onClick={runWhatIf} className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">
              Project
            </button>
          </div>
          {whatIf && (
            <div className="mt-3 text-sm text-gray-800 dark:text-gray-200" data-testid="what-if-result">
              Over {whatIf.days} days ({whatIf.rows} charges): billed {usd(whatIf.current_billed_usd)} → <strong>{usd(whatIf.projected_billed_usd)}</strong>, cost{' '}
              {usd(whatIf.cost_usd)}, margin {usd(whatIf.current_margin_usd)} → <strong>{usd(whatIf.projected_margin_usd)}</strong>.
            </div>
          )}
        </section>
      )}
    </div>
  );
}
